import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Loader2 } from 'lucide-react';
import { apriPortalePaol, getPaolSedi, getPaolSso } from '../services/praticheAutoOnline';

/**
 * /area-riservata/facile/accedi?sede=<chiave> — accesso a un tocco a Pratiche Auto Online
 * (Partnership Facile.it) per l'app mobile.
 *
 * L'app non può fare il POST verso il portale dal browser in-app: apre questa pagina col
 * ponte autenticato (/accesso-associati?bridge=…&next=…) e la pagina fa ciò che fa il
 * pulsante della card nell'area riservata (PraticheAutoOnlineAccess):
 * - la sede deve essere tra quelle restituite da GET /praticheautoonline/sedi
 *   ('principal' o il sedeId di una secondaria con quota dell'anno);
 * - POST /praticheautoonline/sso-token { sede } e consegna del token con apriPortalePaol
 *   (POST: modulo auto-inviante, token nel corpo; GET: navigazione a redirectUrl).
 * Il token non finisce mai nell'URL di questa pagina né nei log. Prima di lasciare la
 * pagina l'indirizzo diventa /area-riservata: «indietro» dal portale torna alla dashboard
 * invece di ripetere l'accesso.
 */

const TORNA = '/area-riservata';

const MSG = {
  nonAttivo: 'Non appena il nuovo servizio sarà disponibile, gli associati saranno avvisati via email e nel centro notifiche.',
  nonRete: "L'accesso a Pratiche Auto Online è riservato agli studi della Rete Certificata UNASCA attiva.",
  sede: 'La sede indicata non è disponibile per Pratiche Auto Online.',
  servizio: 'Il servizio non è al momento disponibile. Riprova più tardi.',
  rete: 'Collegamento non riuscito. Controlla la connessione e riprova.',
  generico: 'Impossibile aprire Pratiche Auto Online. Riprova.',
};

export const messaggioErroreSso = (e: unknown): string => {
  const r = (e as { response?: { status?: number; data?: { code?: string } } })?.response;
  if (!r) return MSG.rete;
  if (r.status === 503) return MSG.servizio;
  if (r.status === 403) {
    const code = r.data?.code;
    if (code === 'SEDE_NOT_FOUND' || code === 'SEDE_NOT_ELIGIBLE') return MSG.sede;
    return MSG.nonRete;
  }
  return MSG.generico;
};

const FacileAccessoPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [errore, setErrore] = useState<string | null>(null);
  const avviato = useRef(false);

  useEffect(() => {
    // Ritorno dal portale con la pagina ripristinata dalla cache del browser: dashboard.
    const onPageShow = (ev: PageTransitionEvent) => {
      if (ev.persisted) window.location.replace(TORNA);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  useEffect(() => {
    if (avviato.current) return;
    avviato.current = true;
    const richiesta = searchParams.get('sede');
    (async () => {
      try {
        const { enabled, sedi } = await getPaolSedi();
        if (!enabled) return setErrore(MSG.nonAttivo);
        if (!sedi.length) return setErrore(MSG.nonRete);
        const sede = richiesta
          ? sedi.find((s) => s.key === richiesta)
          : sedi.length === 1 ? sedi[0] : undefined;
        if (!sede) return setErrore(MSG.sede);
        const sso = await getPaolSso(sede.key);
        if (sso.method === 'POST' && !sso.ssoUrl) return setErrore(MSG.servizio);
        window.history.replaceState(window.history.state, '', TORNA);
        // Se la navigazione non parte, dopo qualche secondo lo si dice.
        window.setTimeout(() => setErrore(MSG.generico), 8000);
        apriPortalePaol(sso);
      } catch (e) {
        setErrore(messaggioErroreSso(e));
      }
    })();
  }, [searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
      <Helmet>
        <meta name="referrer" content="no-referrer" />
        <meta name="robots" content="noindex, nofollow" />
        <title>Accesso a Facile.it - UNASCA</title>
      </Helmet>
      <div className="max-w-sm w-full text-center bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        {errore ? (
          <>
            <h1 className="text-base font-semibold text-gray-900 dark:text-white">Accesso a Pratiche Auto Online</h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300" role="alert">{errore}</p>
            <Link
              to={TORNA}
              className="mt-4 inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700"
            >
              Torna all&apos;area riservata
            </Link>
          </>
        ) : (
          <div className="flex items-center justify-center gap-2 text-sm text-gray-600 dark:text-gray-300" role="status">
            <Loader2 size={16} className="animate-spin" aria-hidden="true" /> Collegamento a Facile.it in corso…
          </div>
        )}
      </div>
    </div>
  );
};

export default FacileAccessoPage;
